/**
 * RBAC (Role-Based Access Control) Utilities
 * Helper functions for checking permissions and visibility rules
 * Supports both legacy (OWNER, ASSOCIATE, CLERK, CLIENT) and new (owner, admin, partner, assistant, staff, client) roles
 */

import { LegacyUserRole, DataVisibility, getDataVisibilityForRole } from '@/types/auth.types'
import type { UserRole } from '@/types/user.types'
import { ROLE_HIERARCHY } from '@/types/user.types'
import { normalizeRole, resolveRole } from '@/security/accessContract'

// ============================================================================
// ROLE CONVERSION UTILITIES
// ============================================================================

/**
 * Map legacy role to new role system
 */
export function mapLegacyToNewRole(legacyRole: LegacyUserRole | string): UserRole {
  return normalizeRole(legacyRole)
}

/**
 * Map new role to legacy role system
 */
export function mapNewToLegacyRole(newRole: UserRole): LegacyUserRole | null {
  switch (resolveRole(newRole).canonicalRole) {
    case 'firm_owner':
      return LegacyUserRole.OWNER
    case 'firm_admin':
    case 'ca_partner':
      return LegacyUserRole.ASSOCIATE
    case 'technical_assistant':
    case 'research_assistant':
      return LegacyUserRole.CLERK
    case 'individual_client':
    case 'business_owner_client':
    case 'authorized_client':
      return LegacyUserRole.CLIENT
    default:
      return null
  }
}

/**
 * Check if a role is at least as high as another in the hierarchy
 */
export function isRoleAtLeast(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole]
}

// ============================================================================
// FEATURE PERMISSION CHECKERS (Legacy Support)
// ============================================================================

export const canCreateCase = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === 'owner'
}

export const canEditCase = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
         role === 'owner' || role === 'associate'
}

export const canDeleteCase = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === 'owner'
}

export const canArchiveCase = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === 'owner'
}

export const canCreateHearing = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
         role === 'owner' || role === 'associate'
}

export const canEditHearing = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
         role === 'owner' || role === 'associate'
}

export const canAddNotes = (role: LegacyUserRole | string): boolean => {
  return (
    role === LegacyUserRole.OWNER ||
    role === LegacyUserRole.ASSOCIATE ||
    role === LegacyUserRole.CLERK ||
    role === 'owner' || role === 'associate' || role === 'clerk'
  )
}

export const canUploadDocuments = (role: LegacyUserRole | string): boolean => {
  return (
    role === LegacyUserRole.OWNER ||
    role === LegacyUserRole.ASSOCIATE ||
    role === LegacyUserRole.CLERK ||
    role === 'owner' || role === 'associate' || role === 'clerk'
  )
}

export const canViewAuditLog = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === 'owner'
}

export const canEditCaseSummary = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === 'owner'
}

export const canManageUsers = (role: LegacyUserRole | string): boolean => {
  return role === LegacyUserRole.OWNER || role === 'owner'
}

export const canAccessSettings = (_role: LegacyUserRole | string): boolean => {
  return true // All users can access settings
}

// ============================================================================
// NEW ROLE PERMISSION CHECKERS
// ============================================================================

/**
 * Check if a user with the given role can manage users
 */
export function canManageUsersNew(role: UserRole): boolean {
  return isRoleAtLeast(role, 'firm_admin')
}

/**
 * Check if a user with the given role can view audit logs
 */
export function canViewAuditLogNew(role: UserRole): boolean {
  return isRoleAtLeast(role, 'firm_admin')
}

/**
 * Check if a user with the given role can manage settings
 */
export function canManageSettingsNew(role: UserRole): boolean {
  return isRoleAtLeast(role, 'firm_admin')
}

/**
 * Check if a user with the given role can view all sessions
 */
export function canViewAllSessionsNew(role: UserRole): boolean {
  return isRoleAtLeast(role, 'firm_admin')
}

/**
 * Check if a user with the given role can invite users
 */
export function canInviteUsersNew(role: UserRole): boolean {
  return isRoleAtLeast(role, 'firm_admin')
}

/**
 * Check if a user with the given role can manage billing
 */
export function canManageBillingNew(role: UserRole): boolean {
  return resolveRole(role).canonicalRole === 'firm_owner'
}

// ============================================================================
// CASE VISIBILITY FILTERS
// ============================================================================

/**
 * Case Visibility Filter
 * Returns which cases a user should see based on their role and assignments
 */
export function filterCasesByVisibility(
  cases: any[],
  userRole: UserRole,
  userAssignedCases?: string[],
  userOwnCaseId?: string
): any[] {
  const visibility = getDataVisibilityForRole(userRole)

  switch (visibility) {
    case DataVisibility.ALL:
      // Owner sees all cases
      return cases

    case DataVisibility.ASSIGNED:
      // Associate and Clerk see only assigned cases
      return cases.filter((c) => userAssignedCases?.includes(c.id))

    case DataVisibility.OWN:
      // Client sees only their own case
      return cases.filter((c) => c.id === userOwnCaseId)

    default:
      return []
  }
}

// ============================================================================
// SECTION VISIBILITY
// ============================================================================

/**
 * Case Edit Section Visibility
 * Determines which sections of a case can be edited
 */
export const getCaseEditableSections = (role: LegacyUserRole | string) => ({
  overview: role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
            role === 'owner' || role === 'associate',
  hearings: role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
            role === 'owner' || role === 'associate',
  notes: role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
         role === LegacyUserRole.CLERK || role === 'owner' || role === 'associate' || role === 'clerk',
  documents: role === LegacyUserRole.OWNER || role === LegacyUserRole.ASSOCIATE ||
             role === LegacyUserRole.CLERK || role === 'owner' || role === 'associate' || role === 'clerk',
  summary: role === LegacyUserRole.OWNER || role === 'owner',
  auditLog: role === LegacyUserRole.OWNER || role === 'owner',
})

/**
 * Tab Visibility per Role
 * Determines which tabs are visible in case detail
 */
export const getCaseDetailTabs = (role: LegacyUserRole | string) => {
  const baseTabs = ['overview', 'hearings', 'notes', 'documents']

  // Client role doesn't see documents tab
  if (role === LegacyUserRole.CLIENT || role === 'client') {
    return baseTabs.filter((tab) => tab !== 'documents')
  }

  // All other roles see all tabs
  return baseTabs
}

/**
 * Dashboard Card Visibility per Role
 * Determines which cards/sections appear on dashboard
 */
export const getDashboardSections = (role: LegacyUserRole | string) => {
  const canonicalRole = resolveRole(role).canonicalRole

  if (canonicalRole === 'firm_owner' || canonicalRole === 'firm_admin') {
    return {
      overview: true,
      caseMetrics: true,
      recentActivity: true,
      alerts: true,
      userManagement: true,
      settings: true,
    }
  }

  if (canonicalRole === 'ca_partner') {
    return {
      overview: true,
      caseMetrics: true,
      recentActivity: true,
      alerts: true,
      userManagement: false,
      settings: false,
    }
  }

  if (
    canonicalRole === 'technical_assistant' ||
    canonicalRole === 'research_assistant' ||
    role === 'staff'
  ) {
    return {
      overview: true,
      caseMetrics: false,
      recentActivity: true,
      alerts: true,
      userManagement: false,
      settings: false,
    }
  }

  if (
    canonicalRole === 'individual_client' ||
    canonicalRole === 'business_owner_client' ||
    canonicalRole === 'authorized_client' ||
    role === 'client'
  ) {
    return {
      overview: true,
      caseMetrics: false,
      recentActivity: false,
      alerts: false,
      userManagement: false,
      settings: false,
    }
  }

  return {
    overview: true,
    caseMetrics: false,
    recentActivity: false,
    alerts: false,
    userManagement: false,
    settings: false,
  }
}

/**
 * Dashboard sections for new role system
 */
export const getDashboardSectionsNew = (role: UserRole) => {
  const isAdminOrHigher = isRoleAtLeast(role, 'firm_admin')
  const isPartnerOrHigher = isRoleAtLeast(role, 'ca_partner')
  const isAssistantOrHigher = isRoleAtLeast(role, 'research_assistant')

  return {
    overview: true,
    caseMetrics: isPartnerOrHigher,
    recentActivity: isAssistantOrHigher,
    alerts: isAssistantOrHigher,
    userManagement: isAdminOrHigher,
    settings: isAdminOrHigher,
    sessions: isAdminOrHigher,
    auditLog: isAdminOrHigher,
  }
}

// ============================================================================
// NAVIGATION VISIBILITY
// ============================================================================

/**
 * Navigation Item Visibility per Role (Legacy)
 * Determines which main nav items are visible
 */
export const getNavItems = (role: LegacyUserRole | string) => {
  const canonicalRole = resolveRole(role).canonicalRole
  const baseNav = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
    { id: 'customers', label: 'Customers', path: '/customers' },
    { id: 'search', label: 'Search', path: '/search' },
  ]

  const settingsNav = [
    { id: 'settings', label: 'Settings', path: '/settings' },
  ]

  const adminNav = [
    { id: 'users', label: 'Users', path: '/users' },
  ]

  if (canonicalRole === 'firm_owner' || canonicalRole === 'firm_admin') {
    return [...baseNav, ...adminNav, ...settingsNav]
  }

  if (
    canonicalRole === 'ca_partner' ||
    canonicalRole === 'technical_assistant' ||
    canonicalRole === 'research_assistant' ||
    role === 'staff'
  ) {
    return [...baseNav, ...settingsNav]
  }

  if (
    canonicalRole === 'individual_client' ||
    canonicalRole === 'business_owner_client' ||
    canonicalRole === 'authorized_client' ||
    role === 'client'
  ) {
    return [baseNav[0], baseNav[2], ...settingsNav] // Dashboard, Search, and Settings
  }

  return []
}

/**
 * Navigation Item Visibility for new role system
 */
export const getNavItemsNew = (role: UserRole) => {
  const canonicalRole = resolveRole(role).canonicalRole
  const baseNav = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
    { id: 'customers', label: 'Customers', path: '/customers' },
    { id: 'search', label: 'Search', path: '/search' },
  ]

  const settingsNav = [
    { id: 'profile', label: 'Profile', path: '/profile' },
    { id: 'settings', label: 'Settings', path: '/settings' },
  ]

  const adminNav = [
    { id: 'users', label: 'Team', path: '/users' },
    { id: 'sessions', label: 'Sessions', path: '/sessions' },
    { id: 'audit', label: 'Audit Log', path: '/audit-log' },
  ]

  const securityNav = [
    { id: 'security', label: 'Security', path: '/settings/security' },
    { id: 'organization', label: 'Organization', path: '/settings/organization' },
  ]

  if (canonicalRole === 'firm_owner' || canonicalRole === 'firm_admin') {
    return [...baseNav, ...adminNav, ...settingsNav, ...securityNav]
  }

  if (
    canonicalRole === 'ca_partner' ||
    canonicalRole === 'technical_assistant' ||
    canonicalRole === 'research_assistant'
  ) {
    return [...baseNav, ...settingsNav]
  }

  // Client sees limited nav
  return [baseNav[0], ...settingsNav]
}
